import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const PROMO_FORM_URL = "https://readdy.ai/api/form/d73o9jivu4krcbn3aqv0";

export default function ProviderPlan() {
  const { currentUser, session, refreshProfile } = useApp();
  const navigate = useNavigate();
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoSubmitted, setPromoSubmitted] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const currentPlan = currentUser?.plan ?? "free";
  const subscriptionStatus = (currentUser as { subscription_status?: string })?.subscription_status;

  const handleUpgrade = async () => {
    if (!session) { navigate("/login"); return; }
    setUpgradeLoading(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authSession?.access_token}`,
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/mypage/provider?plan=success`,
          cancelUrl: `${window.location.origin}/pricing`,
        }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      if (json.url) window.location.href = json.url;
    } catch (err) {
      alert(`エラーが発生しました: ${(err as Error).message}`);
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleCancelPlan = async () => {
    if (!session || !currentUser?.id) return;
    setCancelLoading(true);
    try {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("stripe_subscription_id")
        .eq("id", currentUser.id)
        .maybeSingle();

      if (!profileData?.stripe_subscription_id) {
        throw new Error("サブスクリプション情報が見つかりません");
      }
      // Update DB to reflect cancellation requested
      await supabase.from("profiles").update({
        subscription_status: "cancellation_requested",
      }).eq("id", currentUser.id);
      await refreshProfile();
      setShowCancelConfirm(false);
      setSuccessMessage("解約を申請しました。次回更新日以降にフリープランへ移行されます。");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      alert(`エラーが発生しました: ${(err as Error).message}`);
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePromoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((v, k) => body.append(k, String(v)));
    await fetch(PROMO_FORM_URL, { method: "POST", body, headers: { "Content-Type": "application/x-www-form-urlencoded" } });
    setPromoSubmitted(true);
    setTimeout(() => { setShowPromoForm(false); setPromoSubmitted(false); }, 3000);
  };

  const isLight = currentPlan === "light";
  const isCancellationRequested = subscriptionStatus === "cancellation_requested";

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-center gap-3">
          <i className="ri-checkbox-circle-line text-green-500 text-xl flex-shrink-0" />
          <p className="text-sm font-sans text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-2xl border border-stone-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-deep-green text-lg">現在のプラン</h3>
          <span className={`text-xs px-3 py-1 rounded-full font-sans ${isLight ? "bg-gold/10 text-gold" : "bg-stone-100 text-charcoal/60"}`}>
            {isLight ? "ライトプラン" : "フリープラン"}
          </span>
        </div>
        {!isLight && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-charcoal/50 font-sans">残り動画投稿枠</span>
              <span className="text-xs text-teal font-sans font-medium">3本まで無料</span>
            </div>
            <p className="text-xs text-charcoal/50 font-sans">動画3本・教材3点まで / 動画販売手数料20% / 教材手数料15%</p>
          </>
        )}
        {isLight && (
          <>
            <p className="text-xs text-charcoal/50 font-sans mb-2">動画・教材 無制限出品 / 動画販売手数料10% / 教材手数料8%</p>
            {isCancellationRequested && (
              <div className="mt-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-xs text-amber-700 font-sans flex items-center gap-1.5">
                  <i className="ri-information-line flex-shrink-0" />
                  解約申請済みです。次回更新日以降にフリープランへ移行されます。
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Plan Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`rounded-2xl border-2 p-6 ${!isLight ? "border-teal/40 bg-teal/5" : "border-stone-200 bg-white"}`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-deep-green text-base">フリープラン</h4>
            {!isLight && <span className="text-xs bg-teal text-white px-2 py-0.5 rounded-full font-sans">現在</span>}
          </div>
          <p className="font-serif text-3xl text-charcoal mb-4">¥0<span className="text-sm font-sans text-charcoal/40">/月</span></p>
          <ul className="space-y-2 text-sm font-sans text-charcoal/70">
            {["動画 3本まで", "教材 3点まで", "動画手数料 20%", "教材手数料 15%", "簡易分析のみ"].map((f) => (
              <li key={f} className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-teal text-base" />{f}</li>
            ))}
          </ul>
          {!isLight && (
            <div className="mt-6 pt-4 border-t border-stone-100">
              <p className="text-xs text-charcoal/50 font-sans mb-3">動画枠の追加（月額1,500円/本）</p>
              <button className="w-full py-2.5 rounded-full border border-teal text-teal text-sm font-sans cursor-pointer hover:bg-teal hover:text-white transition-colors whitespace-nowrap">
                + 追加枠を購入する
              </button>
            </div>
          )}
          {isLight && !isCancellationRequested && (
            <div className="mt-6 pt-4 border-t border-stone-100">
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="w-full py-2.5 rounded-full border border-stone-200 text-charcoal/50 text-sm font-sans cursor-pointer hover:border-red-300 hover:text-red-400 transition-colors whitespace-nowrap"
              >
                フリープランに戻す
              </button>
            </div>
          )}
        </div>

        <div className={`rounded-2xl border-2 p-6 relative overflow-hidden ${isLight ? "border-gold/50 bg-gold/5" : "border-gold/30 bg-white"}`}>
          <div className="absolute top-0 right-0 bg-gold text-white text-xs px-4 py-1 font-sans">おすすめ</div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-deep-green text-base">ライトプラン</h4>
            {isLight && <span className="text-xs bg-gold text-white px-2 py-0.5 rounded-full font-sans">現在</span>}
          </div>
          <p className="font-serif text-3xl text-charcoal mb-4">¥5,000<span className="text-sm font-sans text-charcoal/40">/月</span></p>
          <ul className="space-y-2 text-sm font-sans text-charcoal/70">
            {["動画 無制限", "教材 無制限", "動画手数料 10%（半額）", "教材手数料 8%", "売上・購入者動向分析", "特集掲載候補に追加"].map((f) => (
              <li key={f} className="flex items-center gap-2"><i className="ri-checkbox-circle-fill text-gold text-base" />{f}</li>
            ))}
          </ul>
          {!isLight && (
            <button
              onClick={handleUpgrade}
              disabled={upgradeLoading}
              className="mt-6 w-full py-2.5 rounded-full bg-gold text-white text-sm font-sans cursor-pointer hover:bg-amber-500 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {upgradeLoading ? (
                <><i className="ri-loader-4-line animate-spin" />処理中...</>
              ) : "ライトプランへアップグレード →"}
            </button>
          )}
          {isLight && (
            <div className="mt-6 p-3 bg-green-50 rounded-xl border border-green-100">
              <p className="text-xs text-green-700 font-sans flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line text-green-500 flex-shrink-0" />
                ライトプランで出品中です。Stripeで管理されています。
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Promo Request Section */}
      <div className="bg-ivory rounded-2xl p-6 border border-stone-100">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h4 className="font-serif text-deep-green text-base mb-1">
              <i className="ri-megaphone-line text-gold mr-2" />お知らせ欄への掲載申請
            </h4>
            <p className="text-sm text-charcoal/60 font-sans leading-relaxed">
              イベント・特集・キャンペーン情報をサイトのお知らせ欄に掲載できます。<br />
              申請フォームに内容を送信すると、管理者が確認の上、掲載します。
            </p>
          </div>
          <button
            onClick={() => setShowPromoForm(true)}
            className="text-sm bg-deep-green text-white px-6 py-3 rounded-full font-sans whitespace-nowrap cursor-pointer hover:bg-teal transition-colors flex-shrink-0"
          >
            宣伝を申請する →
          </button>
        </div>
      </div>

      {/* Cancel Confirm Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="text-center mb-5">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                <i className="ri-error-warning-line text-red-400 text-2xl" />
              </div>
              <h3 className="font-serif text-deep-green text-lg mb-2">ライトプランを解約しますか？</h3>
              <p className="text-sm text-charcoal/60 font-sans leading-relaxed">
                解約後、次回更新日以降にフリープランへ移行されます。出品中のデータは保持されます。
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 py-3 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer hover:bg-stone-50 whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={handleCancelPlan}
                disabled={cancelLoading}
                className="flex-1 py-3 rounded-full bg-red-500 text-white text-sm font-sans cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap disabled:opacity-60"
              >
                {cancelLoading ? "処理中..." : "解約を申請する"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promo Modal */}
      {showPromoForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            {promoSubmitted ? (
              <div className="text-center py-8">
                <i className="ri-checkbox-circle-fill text-5xl text-teal block mb-4" />
                <p className="font-serif text-deep-green text-lg">申請を受け付けました</p>
                <p className="text-sm text-charcoal/60 font-sans mt-2">管理者が確認後、お知らせ欄に掲載いたします。</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-serif text-deep-green">宣伝掲載の申請</h3>
                  <button onClick={() => setShowPromoForm(false)} className="w-8 h-8 flex items-center justify-center text-charcoal/40 cursor-pointer hover:text-charcoal">
                    <i className="ri-close-line text-xl" />
                  </button>
                </div>
                <form data-readdy-form id="promo-request-form" onSubmit={handlePromoSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1">事業者名・サービス名 <span className="text-red-400">*</span></label>
                    <input name="businessName" required defaultValue={currentUser?.serviceName || currentUser?.name || ""} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal" />
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1">掲載カテゴリ <span className="text-red-400">*</span></label>
                    <select name="category" required className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal bg-white cursor-pointer">
                      <option value="">選択してください</option>
                      <option value="event">イベント</option>
                      <option value="feature">特集</option>
                      <option value="campaign">キャンペーン</option>
                      <option value="announcement">お知らせ</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1">掲載タイトル <span className="text-red-400">*</span></label>
                    <input name="title" required maxLength={80} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal" />
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1">詳細内容 <span className="text-red-400">*</span></label>
                    <textarea name="detail" required maxLength={500} rows={4} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal resize-none" placeholder="掲載したい内容の詳細（500文字以内）" />
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1">連絡先メールアドレス <span className="text-red-400">*</span></label>
                    <input name="email" type="email" required defaultValue={currentUser?.email || ""} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal" />
                  </div>
                  <button type="submit" className="w-full bg-deep-green text-white py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
                    申請を送信する →
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
